/**
 * lib/fcm.js
 *
 * Firebase Cloud Messaging integration for the web client. Called once
 * per portal session — it:
 *   1. Registers `/firebase-messaging-sw.js` as a service worker.
 *   2. Requests notification permission (silent if already granted).
 *   3. Calls firebase getToken({ vapidKey }) to obtain a device token.
 *   4. POSTs the token to /api/connect/me/fcm-token so the server can
 *      target this browser on future stage_done events.
 *   5. Wires an onMessage listener so foreground pushes surface as a
 *      toast (background pushes are handled by the service worker).
 *
 * All env values are optional — if any are missing, this file becomes a
 * silent no-op and the rest of the app continues normally.
 */

import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { toast } from "sonner";
import api from "@/lib/api";

const CFG = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _app = null;
let _msg = null;
let _initPromise = null;

function _configComplete() {
  return CFG.apiKey && CFG.projectId && CFG.messagingSenderId && CFG.appId && VAPID;
}

async function _init() {
  if (_msg) return _msg;
  if (!_configComplete()) return null;
  if (!(await isSupported().catch(() => false))) return null;
  if (!("serviceWorker" in navigator)) return null;

  // Register the SW at the site root — required by Firebase.
  const swReg = await navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .catch(() => null);
  if (!swReg) return null;

  _app = getApps().length ? getApps()[0] : initializeApp(CFG);
  _msg = getMessaging(_app);

  // Foreground listener — Firebase does NOT show a system notification when
  // the tab is focused, so we surface it as a Sonner toast.
  onMessage(_msg, (payload) => {
    const t = payload?.notification?.title || "Fortune Sourcings";
    const b = payload?.notification?.body  || "";
    toast(t, { description: b, duration: 6000 });
  });

  return _msg;
}

/**
 * enableWebPush — call after login when the user is on any /portal route.
 * Idempotent, deduped, non-blocking.
 */
export async function enableWebPush() {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    try {
      const msg = await _init();
      if (!msg) return { ok: false, reason: "not-configured" };
      if (typeof Notification === "undefined") return { ok: false, reason: "no-notification-api" };
      // If the user already denied, do nothing.
      if (Notification.permission === "denied") return { ok: false, reason: "denied" };
      // Ask only if we haven't been granted yet — this avoids surprise prompts.
      if (Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        if (p !== "granted") return { ok: false, reason: "not-granted" };
      }
      const token = await getToken(msg, { vapidKey: VAPID });
      if (!token) return { ok: false, reason: "no-token" };
      await api.post("/connect/me/fcm-token", { token, platform: "web" }).catch(() => {});
      return { ok: true, token };
    } catch (e) {
      // Silent — push is best-effort; the rest of the portal keeps working.
      return { ok: false, reason: String(e).slice(0, 120) };
    }
  })();
  return _initPromise;
}
